import {
  withLatestFrom,
  exhaustMap,
  filter,
  tap,
} from 'rxjs/operators';
import { UsersAction } from './../users/users.actions';
import { Injectable, NgZone } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AppState } from '../../interfaces/app-state.interface';
import { TrackingActions } from './tracking.action';
import { Router } from '@angular/router';
import { RouterTrackerService } from '../../services/router-tracker/router-tracker.service';
import { of } from 'rxjs';
import { OrderSentEvent } from '../../models/segment/order-sent-event.model';
import { SegmentEvent } from '../../enum/segment/segment-event.enum';
import { TrackingService } from '../../services/tracking/tracking.service';
import { StorageService } from '../../services/storage.service';
import { RolesUser } from '../../enum/roles-user.enum';
import { Roles } from '../../models/roles/roles.enum';

declare const ga: any;
const mixpanelEventNameBlockList: string[] = [
  'Loaded a Element',
  'Fixedincome Filter Used',
  'New Search',
  '[Stocks] My order history clicked',
  'Investment not loaded',
  'Investment Closed',
];

@Injectable()
export class TrackingEffects {
  /**
   * Ao enviar uma ordem na página de Checkout
   * envia para o Segment os dados da ordem.
   *
   * type (Planejada ou Nao Planejada)
   * ticker (PETR4)
   * origin (Recomendacao, Notificacao de Recomendacao, Explore o Mercado, Analise)
   * direction (Compra ou Venda)
   * product_type (Bovespa ou BMF)
   */

  sendOrderInfomation$ = createEffect(() => this.actions$.pipe(
    ofType(TrackingActions.sendOrderInformation),
    withLatestFrom(
      this.store.select((state) => state.authentication.currentUser)
    ),
    filter(([action, currentUser]: [any, any]) => action.payload && currentUser),
    tap(([action, currentUser]: [any, any]) => {
      const windowPage: any = window;
      const event = action.payload as OrderSentEvent;

      event['Is Toro App'] = event.isToroApp ? event.isToroApp : false;
      delete event.isToroApp;

      this.tracking.track(SegmentEvent.OrderSent, event);
      this.tracking.alias(currentUser.userId);
    })
  ), { dispatch: false });

  /**
   * Ao fazer login como usuário ativo pela primeira vez
   * envia para o Segment os dados do usuário.
   *
   * cblc (CBLC do usuário)
   */
  sendFirstLoginActivated$ = createEffect(() => this.actions$.pipe(
    ofType(TrackingActions.sendFirstLoginActivated),
    filter((action: any) => action.payload),
    tap((action: any) => {
      this.ngzone.runOutsideAngular(() => {
        const windowPage: any = window;
        if (windowPage.dataLayer) {
          windowPage.dataLayer.push({
            event: 'Activated User',
            order_id: action.payload.cblc,
          });
        }
      });
    })
  ), { dispatch: false });

  /**
   * Ao receber uma mensagem de erro, envia para o segment
   *
   * message (Mensagem de erro)
   */
  trackErrorMessages$ = createEffect(() => this.actions$.pipe(
    ofType(TrackingActions.trackErrorMessages),
    filter((action: any) => action.payload),
    exhaustMap((action) => {
      this.tracking.track('Processing Error', {
        message: action.payload.message,
      });

      return of(action);
    })
  ));

  /**
   * Atualiza dados das páginas no Segment e Google Tag Manager
   * (Primeiro evento é ignorado para não duplicar com o enviado ao carregar o script)
   */
  updateDataPage$ = createEffect(() => this.actions$.pipe(
    ofType(TrackingActions.updateDataPage),
    withLatestFrom(
      this.store.select((state) => !!state.authentication.currentUser),
      this.store.select((state) => state.userProfile.profile)
    ),
    tap(([action, isLoggedin, profile]) => {
      this.ngzone.runOutsideAngular(() => {
        const windowPage: any = window;

        // Evento de "page view customizado" com analytics
        if ((window as any).ga) {
          (window as any).ga('set', 'dimension1', 'isLoggedin');
          ga('send', 'pageview', { dimension1: isLoggedin });
        }

        // Page view com Tag Manager
        if (
          windowPage.dataLayer &&
          this.routerTracker.getHistory().length > 1
        ) {
          windowPage.dataLayer.push({
            event: 'Page View',
            url: `${windowPage.location.origin}${this.router.url}`,
          });
        }
      });
    })
  ), { dispatch: false });

  /**
   * Atualiza dados genéricos no Segment
   *
   * @param events <Object>: Lista de eventos a serem enviadas para o segment, como por exemplo:
   *      { userLogin: '00000000000', element: 'Mais Detalhes' }
   * @param type <String>: Nome do evento a ser enviado para o segment, como por exemplo:
   *      'Interaction'
   */
  trackEvents$ = createEffect(() => this.actions$.pipe(
    ofType(TrackingActions.trackEvents),
    withLatestFrom(
      this.store.select((state) => state.userProfile),
      this.store.select((state) => state.authentication),
      this.store.select((state) => state.suitability)
    ),
    filter(([action]) => (action as any).payload.events && (action as any).payload.type),
    tap(([action, userProfile, authentication, suitability]) => {
      //TODO(PM's): Removendo temporariamente por causa do excesso de eventos no mixpanel, validar com PM's quando retornar!
      if (mixpanelEventNameBlockList.includes((action as any).payload.type)) {
        return;
      }

      const profileInfo = userProfile.profile.isTrader
        ? userProfile.profile.profileInfo.typeProfile || 'Trader'
        : 'NTrader';

      const typedAction = action as any;
      const updatedEvents = {
        ...typedAction.payload.events,
        profile: profileInfo,
      };

      // Injeta home_type + user_type nos eventos de Home (Product Area Origin iniciando em "Home"),
      // preservando qualquer Specific Properties já enviado manualmente pelo componente.
      const productAreaOrigin = typedAction.payload.events['Product Area Origin'];
      const isHomeEvent =
        typeof productAreaOrigin === 'string' && productAreaOrigin.startsWith('Home');

      if (isHomeEvent) {
        const cookieTrader = StorageService.getCookie('hubUserTraderStatus') === 'true';
        const cookieExclusive = StorageService.getCookie('hubUserExclusiveStatus') === 'true';
        const userType = cookieExclusive ? 'Exclusivo' : cookieTrader ? 'Trader' : 'Varejo';
        const homeType =
          userProfile.user && userProfile.user.profile === RolesUser.Activated
            ? 'Ativa'
            : 'Aberta';

        const existingSpecificProperties = typedAction.payload.events['Specific Properties'];
        updatedEvents['Specific Properties'] = {
          home_type: homeType,
          user_type: userType,
          ...(existingSpecificProperties && typeof existingSpecificProperties === 'object'
            ? existingSpecificProperties
            : {}),
        };
      }

      const loggedUser = userProfile && (userProfile as any).user;
      const currentRole =
        authentication.currentUser && (authentication.currentUser as any).role;
      const isRepresentation =
        (loggedUser && loggedUser.isRepresentation === true) ||
        currentRole === Roles.Attorney;
      const userRole = loggedUser
        ? isRepresentation
          ? 'Procurador'
          : 'Titular'
        : undefined;

      const events = {
        path: window.location.pathname,
        cblc: authentication.currentUser && authentication.currentUser.cblc,
        user_id: userProfile.user && userProfile.user.id,
        distinct_id: userProfile.user && userProfile.user.id,
        customer_status: userProfile.user && userProfile.user.profile,
        registration_status:
          userProfile.user && userProfile.user.registrationStatus,
        suitability_profile:
          suitability.profile && suitability.profile.description,
        user_role: userRole,
        ['Is Toro App']: false,
        ...updatedEvents,
      };

      const eventWithoutUndefined = Object.entries(events).reduce(
        (agg, [key, value]) => {
          if (value !== undefined) {
            return { ...agg, [key]: value };
          }
          return agg;
        },
        {}
      );

      const normalizedEvent = eventWithoutUndefined as Record<string, unknown>;
      if (Object.prototype.hasOwnProperty.call(normalizedEvent, 'Time Responsavel')) {
        if (normalizedEvent['Time Responsável'] === undefined) {
          normalizedEvent['Time Responsável'] = normalizedEvent['Time Responsavel'];
        }
        delete normalizedEvent['Time Responsavel'];
      }

      this.tracking.track(typedAction.payload.type, normalizedEvent);
    })
  ), { dispatch: false });

  /**
   * Envia dados para o Google Analytics de quando o usuário faz o Cadastro
   */
  trackFormEvents$ = createEffect(() => this.actions$.pipe(
    ofType(TrackingActions.trackFormEvent),
    filter(
      (action: any) =>
        action.payload.type &&
        action.payload.event &&
        action.payload.event !== this.lastTrackedEvent
    ),
    tap((action: any) => {
      this.lastTrackedEvent = action.payload.event;
      if ((window as any).ga) {
        (window as any).ga('send', 'event', {
          url: `${window.location.origin}${this.router.url}`,
          eventLabel: action.payload.event,
          eventAction: action.payload.type,
          eventCategory: 'Cadastro',
        });
      }
    })
  ), { dispatch: false });

  /**
   * Envia dados para o Segment de quando o usuário faz Login
   */
  sendTrackLogin$ = createEffect(() => this.actions$.pipe(
    ofType(TrackingActions.sendTrackLogin),
    withLatestFrom(
      this.store.select((state) => state.authentication.isUserLogin),
      this.store.select((state) => state.userProfile.profile)
    ),
    filter(([action]) => (action as any).payload.userId),
    tap(([action, user, profile]) => {
      if (user) {
        this.tracking.identify((action as any).payload.userId);
        this.tracking.peopleSet((action as any).payload.events);

        this.store.dispatch({
          type: UsersAction.isUserLogin,
          payload: false,
        });
      }
    })
  ), { dispatch: false });

  /**
   * Envia dados para o Google Analytics de quando o usuário faz Login
   */
  trackLoginEvents$ = createEffect(() => this.actions$.pipe(
    ofType(TrackingActions.trackLoginEvent),
    filter(
      (action) =>
        (action as any).payload.type &&
        (action as any).payload.event &&
        (action as any).payload.event !== this.lastTrackedEvent
    ),
    exhaustMap((action: any) => {
      this.lastTrackedEvent = action.payload.event;
      if (!(window as any).ga) return of(false);
      (window as any).ga('send', 'event', {
        url: `${window.location.origin}${this.router.url}`,
        eventLabel: action.payload.event,
        eventAction: action.payload.type,
        eventCategory: 'Login',
      });

      return of(true);
    })
  ), { dispatch: false });

  /**
   * Envia dados para o Google Analytics.
   *
   * @param url: string = a url em que o usuário se encontra e onde o GA será acionado
   * @param eventLabel: string = valor a ser enviado para o GA.
   * @param eventAction: string = valor que informa ao GA qual evento foi trackeado. Ex: Search, Filter ou Click.
   * @param eventCategory: string = normalmente é a página onde o GA será acionado. Ex: Login, Fixed Income, Cadastro.
   * @param eventValue: string = Valor a ser informado para o GA referente a página.
   */
  trackingGAEvents$ = createEffect(() => this.actions$.pipe(
    ofType(TrackingActions.trackEventsToGA),
    filter(
      (action) =>
        (action as any).payload.type &&
        (action as any).payload.event &&
        (action as any).payload.event !== this.lastTrackedEvent
    ),
    exhaustMap((action: any) => {
      this.lastTrackedEvent = action.payload.event;
      const gaObject = {
        url: `${window.location.origin}${this.router.url}`,
        eventLabel: action.payload.event,
        eventAction: action.payload.type,
        eventCategory: action.payload.category,
      };
      const value = action.payload.value;
      if (value) {
        // tslint:disable-next-line: no-string-literal
        gaObject['eventValue'] = action.payload.value;
      }
      ga('send', 'event', gaObject);
      return of(true);
    })
  ), { dispatch: false });

  private lastTrackedEvent = '';

  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private ngzone: NgZone,
    private router: Router,
    private routerTracker: RouterTrackerService,
    private tracking: TrackingService
  ) {}
}
